import React, { useRef, useState, useEffect } from 'react';
import { Container } from '@plone/components';
import Anontools from '@plone/volto/components/theme/Anontools/Anontools';
import LanguageSelector from '@plone/volto/components/theme/LanguageSelector/LanguageSelector';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import FontSizeControls from './AccessibilityControls/FontSizeControls';
import HoverReaderControls from './AccessibilityControls/HoverReaderControls';
import DropdownMenu from './DropdownMenu';
import SlotRenderer from '@plone/volto/components/theme/SlotRenderer/SlotRenderer';
import { useSelector, shallowEqual } from 'react-redux';
import type { Content } from '@plone/types';
import { Icon } from '@plone/volto/components';
import moreIcon from '@plone/volto/icons/more.svg';
import { useAutoCollapse } from './hooks/useAutoCollapse';

const accessibilityControls = (
  <>
    <LanguageSelector />
    <ThemeToggle />
    <HoverReaderControls />
    <FontSizeControls />
  </>
);

const anonTools = (
  <div className="header-accessibility__tools">
    <Anontools />
  </div>
);

type AccessibilityBarProps = {
  token?: string | null;
  className?: string;
  isMobileLayout?: boolean;
};

const AccessibilityBar: React.FC<AccessibilityBarProps> = ({
  token,
  className = '',
  isMobileLayout = false,
}) => {
  const content = useSelector<any, Content>(
    (state: any) => state.content?.data,
    shallowEqual,
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { shouldCollapse } = useAutoCollapse({
    containerRef,
    isMobileLayout,
    dependencies: [content],
  });

  useEffect(() => {
    if (isMobileLayout) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsMenuOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMobileLayout]);

  const secondaryLinks = (
    <>
      <DropdownMenu
        title="ACESSO RÁPIDO"
        links={[
          {
            title: 'e-Processos',
            href: 'https://e-processos.camarauberlandia.mg.gov.br/default_index_html',
          },
          {
            title: 'Acesso Restrito',
            href: 'https://sistema.camarauberlandia.mg.gov.br/GRP/login',
          },
          { title: 'TV Câmara', href: '/imprensa/tv-camara' },
          { title: 'Vereadores', href: '/vereadores' },
          { title: 'E-SIC', href: '/transparencia/e-sic' },
        ]}
      />
    </>
  );

  const socialRenderer = (
    <div className="header-accessibility__social">
      <SlotRenderer name="followUs" content={content} location={undefined} />
    </div>
  );

  const actionsContent = (
    <>
      {secondaryLinks}
      {accessibilityControls}
      {anonTools}
    </>
  );

  const renderDropdownMenu = (isMobile: boolean) => {
    const button = (
      <button
        type="button"
        className={
          isMobile
            ? 'mobile-accessibility-button'
            : 'header-dropdown-button hamburger-button'
        }
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-expanded={isMenuOpen}
        aria-label={
          isMobile ? 'Opções de Acessibilidade' : 'Menu de Acessibilidade'
        }
      >
        {isMobile ? (
          <Icon name={moreIcon} size="40px" />
        ) : (
          <>
            <span>MENU</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </>
        )}
      </button>
    );

    const menu = isMenuOpen && (
      <div
        className={
          isMobile
            ? 'mobile-accessibility-menu'
            : 'header-dropdown-list collapsed-actions-menu'
        }
      >
        {isMobile ? (
          <div className="mobile-accessibility-menu-inner">
            <div className="mobile-accessibility-top-controls">
              {accessibilityControls}
            </div>
            <div className="mobile-extra-links">{secondaryLinks}</div>
            {anonTools}
            {socialRenderer}
          </div>
        ) : (
          <>
            <div className="desktop-collapsed-accessibility-controls">
              {accessibilityControls}
            </div>
            {secondaryLinks}
            {anonTools}
          </>
        )}
      </div>
    );

    if (isMobile) {
      return (
        <>
          {button}
          {menu}
        </>
      );
    }

    return (
      <div className="header-dropdown-menu header-accessibility__dropdown">
        {button}
        {menu}
      </div>
    );
  };

  if (isMobileLayout) {
    return (
      <div
        className={`mobile-accessibility-dropdown ${className}`}
        ref={dropdownRef}
      >
        {renderDropdownMenu(true)}
      </div>
    );
  }

  return (
    <div className={`header-accessibility-bar ${className}`}>
      <Container layout>
        <div className="header-accessibility" ref={containerRef}>
          {socialRenderer}

          <div
            className="header-accessibility__actions header-accessibility__actions-measurer"
            aria-hidden="true"
          >
            {actionsContent}
          </div>

          <div
            className={`header-accessibility__actions ${shouldCollapse ? 'is-collapsed' : ''}`}
          >
            {shouldCollapse ? renderDropdownMenu(false) : actionsContent}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default AccessibilityBar;
